import * as React from 'react';
import { useState, useEffect } from "react";
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import './MultiDropdownStyles.css';
import { VariableSizeList } from 'react-window';
import { useMediaQuery, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

const LISTBOX_PADDING = 4;

const getSearchTerms = (text, options = []) => {
    const query = text.trim().toLowerCase();
    // Preserve description phrases; split whitespace-separated IDs and known codes.
    const values = query.split(/[\s,;]+/).filter(Boolean);
    const isNumberList = values.length > 1 && values.every(value => /^\d+$/.test(value));
    const isCodeList = values.length > 1 && values.every(value => /^[a-z0-9_-]+$/.test(value)) &&
        values.some(value => options.some(option =>
            [option.value, option.title, String(option.title ?? '').split('|')[0]].some(code =>
                String(code ?? '').trim().toLowerCase() === value
            )
        ));
    const separator = isNumberList || isCodeList ? /[\s,;]+/ : /[,;\r\n\t]+/;
    return query.split(separator).map(term => term.trim()).filter(Boolean);
};

function renderRow(props) {
    const { data, index, style } = props;
    const item = data[index];
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px 16px',
        // overflow: 'hidden',
        whiteSpace: 'normal', // allow wrapping
    };
    return React.cloneElement(item, {
        style: inlineStyle,
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const StyledUl = React.forwardRef(({ style, ...props }, ref) => (
    <ul
        ref={ref}
        style={{
            ...style,
            width: '80%',
            listStyle: 'none',
            overflowY: 'auto',
        }}
        {...props}
    />
));

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, showApply, onApply, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));

    const getChildSize = (child) => {
        const text = child?.props?.children?.[1] || "";
        const approxLines = Math.ceil(text.length / 20);
        return 52;
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref} style={{ width: '100%' }}>
            <OuterElementContext.Provider value={other} >
                <VariableSizeList
                    itemData={itemData}
                    height={200}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType={StyledUl}
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
            {showApply && (
                <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size="small"
                        variant="contained"
                        sx={{ color: "#ffffff" }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={onApply}
                    >
                        Apply
                    </Button>
                </div>
            )}
        </div>
    );
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Opt in only where multi-value searching and filtered Select All are required.
export const MultiDropdown = ({ id, options, onChangeItems, label, selectedItemsData, dropdownWidth, dropdownHeight, name, disabled, onApplyItems, showApply, onOpenDropdown, enableMultiValueSearch = false }) => {
    const [selectedItems, setSelectedItems] = useState(selectedItemsData || []);
    const [openModal, setOpenModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const searchTerms = getSearchTerms(inputValue, options || []);
    const matchingOptions = enableMultiValueSearch ? (options || []).filter(option => {
        const title = String(option.title ?? '').toLowerCase();
        const optionName = String(option.name ?? '').toLowerCase();
        return searchTerms.length === 0 || searchTerms.some(term => title.includes(term) || optionName.includes(term));
    }) : [];
    const allMatchingSelected = matchingOptions.length > 0 && matchingOptions.every(option =>
        selectedItems.some(item => item.title === option.title)
    );

    useEffect(() => {
        setSelectedItems(selectedItemsData || []);
        setSelectAll((selectedItemsData || []).length === options?.length);
    }, [selectedItemsData, options?.length]);
    const handleChange = (event, value, reason) => {

        if (value?.some((item) => item?.title === "Select All")) {
            if (enableMultiValueSearch) {
                handleSelectAllToggle();
                return;
            }
            if (selectAll) {
                setSelectedItems([]);
                setSelectAll(false);
            } else {
                setSelectedItems(options);
                setSelectAll(true);
            }
        } else {
            setSelectedItems(value);
            setSelectAll(value?.length === options?.length);
        }

        onChangeItems(name, value);
        if (showApply && Array.isArray(value) && value.length === 0) {
            if (typeof onApplyItems === "function") {
                onApplyItems(name, []);
            }
        }
    };

    const handleRemoveChip = (chipToRemove) => {
        const newSelectedItems = selectedItems?.filter(
            (item) => item.title !== chipToRemove?.title
        );
        setSelectedItems(newSelectedItems);
        setSelectAll(false);
        onChangeItems(name, newSelectedItems);
        if (showApply && newSelectedItems.length === 0) {
            if (typeof onApplyItems === "function") {
                onApplyItems(name, []);
            }
        }
    };

    const handleClickInside = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSelectAllToggle = () => {
        if (!enableMultiValueSearch) {
            const newSelectedItems = selectAll ? [] : options;
            setSelectedItems(newSelectedItems);
            setSelectAll(!selectAll);
            onChangeItems(name, newSelectedItems);
            return;
        }
        const matchingTitles = new Set(matchingOptions.map(option => option.title));
        const newSelectedItems = allMatchingSelected
            ? selectedItems.filter(item => !matchingTitles.has(item.title))
            : [...selectedItems, ...matchingOptions.filter(option =>
                !selectedItems.some(item => item.title === option.title)
            )];
        setSelectedItems(newSelectedItems);
        setSelectAll(newSelectedItems.length === options?.length);
        onChangeItems(name, newSelectedItems);
        if (showApply && newSelectedItems.length === 0 && typeof onApplyItems === 'function') {
            onApplyItems(name, []);
        }
    };

    const handleCloseDropdown = () => {
        setOpenDropdown(false);
        if (showApply) {
            return;
        }
        setSelectedItems(selectedItemsData || []);
        setSelectAll((selectedItemsData || []).length === options?.length);
    };

    const handleApply = () => {
        if (typeof onApplyItems === "function") {
            onApplyItems(name, selectedItems);
        }
        setOpenModal(false);
        setOpenDropdown(false);
    };

    const allOptions = options ? [{ title: "Select All", value: true }, ...options] : [];

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const pastedValues = enableMultiValueSearch
                ? getSearchTerms(text, options || [])
                : text.split(/[\n,]+/).map(val => val.trim()).filter(Boolean);

            const matchedOptions = options.filter(option => {
                if (!enableMultiValueSearch) {
                    const [numberPart] = option.title.split('|');
                    return pastedValues.includes(option.title) || pastedValues.includes(numberPart);
                }
                const title = String(option.title ?? '').trim().toLowerCase();
                const [numberPart] = title.split('|');
                return pastedValues.includes(title) || pastedValues.includes(numberPart.trim());
            });

            if (matchedOptions.length > 0) {
                setSelectedItems(matchedOptions);
                setSelectAll(matchedOptions.length === options.length);
                onChangeItems(name, matchedOptions);
            } else {
                console.log("No matching options found");
            }
        } catch (err) {
            console.error("Failed to read clipboard: ", err);
        }
    };

    return (
        <div>
            <Autocomplete
                ListboxComponent={ListboxComponent}
                ListboxProps={{ showApply, onApply: handleApply }}
                multiple={true}
                open={openDropdown}
                onOpen={() => {
                    setOpenDropdown(true);
                    if (typeof onOpenDropdown === "function") {
                        onOpenDropdown(name);
                    }
                }}
                onClose={handleCloseDropdown}
                disabled={disabled || false}
                id={id || "checkboxes-tags-demo"}
                name={name || "autoComplete_name"}
                options={allOptions}
                disableCloseOnSelect
                value={selectedItems}
                {...(enableMultiValueSearch ? {
                    inputValue,
                    onInputChange: (event, value, reason) => {
                        // MUI resets the input when selecting/deselecting an option.
                        // Keep the active search until the user edits or clears it.
                        if (reason === 'input' || reason === 'clear') {
                            setInputValue(value);
                        }
                    },
                } : {})}
                onChange={handleChange}
                isOptionEqualToValue={(option, value) => option?.title === value?.title}
                getOptionLabel={(option) => option?.title}
                filterOptions={(availableOptions, { inputValue: query }) => {
                    if (enableMultiValueSearch) {
                        return matchingOptions.length > 0
                            ? [{ title: 'Select All', value: true }, ...matchingOptions]
                            : [];
                    }
                    return availableOptions.filter(option =>
                        option.title.toLowerCase().includes(query.toLowerCase()) ||
                        (option.name && option.name.toLowerCase().includes(query.toLowerCase()))
                    );
                }}
                renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isItemSelected = selectedItems?.some(item => item?.title === option?.title);
                    if (option?.title === 'Select All') {
                        return (
                            <li key={key} {...optionProps} onClick={handleSelectAllToggle}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={enableMultiValueSearch ? allMatchingSelected : selectAll}
                                    indeterminate={enableMultiValueSearch && !allMatchingSelected && matchingOptions.some(option =>
                                        selectedItems.some(item => item.title === option.title)
                                    )}
                                />
                                {option?.title}
                            </li>
                        );
                    }

                    return (
                        <li key={key} {...optionProps} >
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={isItemSelected}
                            />
                            {option?.title}
                        </li>
                    );
                }}
                style={{ maxWidth: dropdownWidth || "100%" }}
                renderInput={(params) => <TextField {...params} disabled={disabled || false} label={label} placeholder="Search" onPaste={(event) => {
                    if (!enableMultiValueSearch) {
                        return;
                    }
                    const terms = getSearchTerms(event.clipboardData.getData('text'), options || []);
                    if (terms.length > 1) {
                        // Normalize before single-line inputs discard Excel line breaks.
                        event.preventDefault();
                        setInputValue(terms.join(terms.every(term => /^\d+$/.test(term)) ? ' ' : ', '));
                        if (!openDropdown) {
                            setOpenDropdown(true);
                            if (typeof onOpenDropdown === 'function') {
                                onOpenDropdown(name);
                            }
                        }
                    }
                }} onClick={() => {
                    setSelectAll(selectedItems?.length === options?.length)

                }} InputProps={{
                    ...params.InputProps,
                    endAdornment: enableMultiValueSearch && inputValue.length > 0 && selectedItems.length === 0
                        ? React.cloneElement(params.InputProps.endAdornment, {},
                            <IconButton
                                key="clear-search"
                                aria-label="Clear search"
                                title="Clear search"
                                size="small"
                                disabled={disabled || false}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setInputValue('');
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>,
                            params.InputProps.endAdornment.props.children
                        )
                        : params.InputProps.endAdornment,
                }} />}
                renderTags={(selected, getTagProps) => {
                    if (selectedItemsData?.length > 0 && selectedItems?.length > 0) {
                        return (
                            <Box
                                onClick={handleClickInside}
                                style={{
                                    // height: "30px",
                                    width: "100%",
                                    display: 'flex',
                                    // overflowY: "scroll",
                                    flexWrap: "wrap",
                                    // gap: "4px",
                                    // padding: "8px",
                                    // border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    backgroundColor: "white",
                                    maxWidth: "50px"
                                }}
                            >
                                {/* {selected?.map((option, index) => (
                                    <Chip
                                        label={option?.title}
                                        {...getTagProps({ index })}
                                        key={index}
                                        onDelete={() => handleRemoveChip(option)}
                                    />
                                ))} */}
                                <span>{selected?.length === 1 ?
                                    <span
                                        style={{
                                            maxWidth: "50px",      // restrict width
                                            overflow: "hidden",    // hide overflow
                                            textOverflow: "ellipsis", // show "..."
                                            whiteSpace: "nowrap",  // prevent wrapping
                                            display: "inline-block",
                                            verticalAlign: "middle"
                                        }}
                                    >{`${selected?.[0]?.title}`}</span> : `${selected?.length}+`}</span>
                            </Box>
                        )
                    } else {
                        return null;
                    }
                }}
            />
            <DialogActions className='pasteClipboard'>
                <Button className='clipboardButton' onClick={handlePasteFromClipboard} color="secondary">Paste from Clipboard</Button>
            </DialogActions>
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Selected Options ({selectedItems?.length})</DialogTitle>
                <DialogContent sx={{ maxHeight: "300px", overflowY: "auto" }}>
                    {selectedItems?.length > 0 ? (
                        selectedItems?.map((option, index) => (
                            <Chip
                                key={index}
                                label={option.title}
                                sx={{ margin: "4px" }}
                                onDelete={() => handleRemoveChip(option)}
                            />
                        ))
                    ) : (
                        <p>No items selected</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};